import {setReservationAvailability} from "../api/reservation_api";
import {markPropertyReservedByMe} from "../state/reserved_by_me_actions";
import {IReservationError, ReservationErrorKind, ReservationPhase} from "../types/IReservationError";
import {IReservationFlowEngineContext, IReservationFlowRun} from "../types/IReservationFlowEngine";
import {IReservationRequest} from "../types/IReservationRequest";
import {ReservationAvailabilityStatus} from "../types/ReservationAvailabilityStatus";
import {IReservationConfirmAttempt, ReservationFlowState} from "../types/ReservationFlowState";
import {callWithCsrfRetry} from "./call_with_csrf_retry";
import {getNameSaveErrorMessage} from "./get_name_save_error_message";
import {isProfileNameFailure} from "./get_reservation_code_step_view";
import {createEmptyReservationDigits} from "./reservation_code_digits";
import {recordReservationCodeSend} from "./reservation_code_send_storage";

// what the CTA shows once the modal is closed: "taken" turns it into "reserved", "unavailable" hides it
const AVAILABILITY_AFTER_ERROR: Partial<Record<ReservationErrorKind, ReservationAvailabilityStatus>> = {
    taken: "taken",
    not_reservable: "unavailable"
};

const isCurrentRun = (ctx: IReservationFlowEngineContext, run: IReservationFlowRun) => run === ctx.runRef.current;

const callApi = <T>(ctx: IReservationFlowEngineContext, call: () => Promise<T>, phase: ReservationPhase, run: IReservationFlowRun) =>
    callWithCsrfRetry({call, phase, budget: run, isCurrent: () => isCurrentRun(ctx, run)});

const clearDigitsAndRequestFocus = (ctx: IReservationFlowEngineContext) => {
    ctx.codeEntry.setDigits(createEmptyReservationDigits());
    ctx.codeEntry.requestFocus();
};

const handleFailure = (ctx: IReservationFlowEngineContext, failedRequest: IReservationRequest, error: IReservationError, next: ReservationFlowState) => {
    ctx.tracking.failed(failedRequest, error);

    const availabilityAfterError = AVAILABILITY_AFTER_ERROR[error.kind];

    if (availabilityAfterError) {
        ctx.dispatch(setReservationAvailability(failedRequest.propertyId, availabilityAfterError));
    }

    if (error.kind === "unauthenticated") {
        // the resumed run continues this funnel, so it must not report another lead_form_start
        ctx.latestRef.current.onUnauthenticated({...failedRequest, isLeadFormStartTracked: true});
    }

    if (isProfileNameFailure(next)) {
        ctx.nameFormActions.rejected(error.message);
    }
};

const handleInitiateFailure = (ctx: IReservationFlowEngineContext, initiateRequest: IReservationRequest, error: IReservationError, isResend: boolean) => {
    const next = ctx.transitions.apply({type: isResend ? "RESEND_FAILURE" : "INITIATE_FAILURE", error, now: Date.now()});

    // the resend link had the focus and was disabled while the code was on its way - the inputs take it once they are enabled
    if (next.status === "code_entry") {
        ctx.codeEntry.requestFocus();
    }

    handleFailure(ctx, initiateRequest, error, next);
};

export const initiateReservation = async (ctx: IReservationFlowEngineContext, initiateRequest: IReservationRequest, isResend: boolean) => {
    const run = ctx.runRef.current;
    const result = await callApi(ctx, () => ctx.latestRef.current.postInitiate({property: initiateRequest.propertyId}).unwrap(), "initiate", run);

    if (result.error) {
        if (isCurrentRun(ctx, run)) {
            handleInitiateFailure(ctx, initiateRequest, result.error, isResend);
        }

        return;
    }

    // counted even when the modal has been closed in the meantime - the code is on its way
    const sentAt = Date.now();
    const codesSent = recordReservationCodeSend(initiateRequest.propertyId, sentAt);

    if (!isCurrentRun(ctx, run)) {
        return;
    }

    const {reservation_handle: handle, expires_in: expiresIn} = result.data;
    const next = ctx.transitions.applyChange({type: isResend ? "RESEND_SUCCESS" : "INITIATE_SUCCESS", handle, expiresIn, codesSent, now: sentAt});

    clearDigitsAndRequestFocus(ctx);

    if (!isResend) {
        ctx.tracking.codeSent(initiateRequest);
    } else if (next !== null) {
        ctx.codeEntry.setIsNewCodeSent(true);
    }
};

export const confirmReservation = async (ctx: IReservationFlowEngineContext, confirmRequest: IReservationRequest, attempt: IReservationConfirmAttempt) => {
    const run = ctx.runRef.current;
    const {handle, code} = attempt;
    const result = await callApi(ctx, () => ctx.latestRef.current.postConfirm({reservation_handle: handle, code}).unwrap(), "confirm", run);

    if (result.error) {
        if (!isCurrentRun(ctx, run)) {
            return;
        }

        const next = ctx.transitions.apply({type: "CONFIRM_FAILURE", error: result.error, now: Date.now()});

        if (next.status === "code_entry") {
            clearDigitsAndRequestFocus(ctx);
        }

        handleFailure(ctx, confirmRequest, result.error, next);
        return;
    }

    // the reservation exists even when the modal has been closed in the meantime
    const applicationUuid = result.data?.application_uuid || null;
    ctx.dispatch(markPropertyReservedByMe(confirmRequest.propertyId, Date.now()));
    // the cached availability follows too, so the CTA stays reserved once the marker is cleared, e.g. on logout before the redirect
    ctx.dispatch(setReservationAvailability(confirmRequest.propertyId, "taken"));

    if (isCurrentRun(ctx, run)) {
        ctx.transitions.apply({type: "CONFIRM_SUCCESS", applicationUuid});
    }

    ctx.tracking.confirmed(confirmRequest, applicationUuid);
};

// failed and retryable: confirm again with the same code while its handle is valid, otherwise initiate again
export const retryReservation = (ctx: IReservationFlowEngineContext) => {
    const {request: retryRequest} = ctx.latestRef.current;

    if (retryRequest === null) {
        return;
    }

    const next = ctx.transitions.applyCallStart({type: "RETRY", now: Date.now()});

    if (next === null) {
        return;
    }

    if (next.status === "confirming") {
        void confirmReservation(ctx, retryRequest, next);
        return;
    }

    void initiateReservation(ctx, retryRequest, false);
};

export const saveReservationName = async (ctx: IReservationFlowEngineContext, name: string, run: IReservationFlowRun) => {
    // a missing CSRF cookie is set again as for the reservation calls
    const result = await callApi(ctx, () => ctx.latestRef.current.patchUserProfile({name}).unwrap(), "initiate", run);

    // closed in the meantime - a code must not be sent for a reservation nobody can finish
    if (!isCurrentRun(ctx, run) || !ctx.isMountedRef.current) {
        return;
    }

    if (result.error) {
        ctx.nameFormActions.saveFailed(getNameSaveErrorMessage(result.error.raw.data));
        return;
    }

    ctx.nameFormActions.saved(name);
    retryReservation(ctx);
};
import {setReservationAvailability} from "../api/reservation_api";
import {markPropertyReservedByMe} from "../state/reserved_by_me_actions";
import {IReservationError, ReservationErrorKind, ReservationPhase} from "../types/IReservationError";
import {IReservationFlowEngineContext, IReservationFlowRun} from "../types/IReservationFlowEngine";
import {IReservationRequest} from "../types/IReservationRequest";
import {ReservationAvailabilityStatus} from "../types/ReservationAvailabilityStatus";
import {IReservationConfirmAttempt, ReservationFlowState} from "../types/ReservationFlowState";
import {callWithCsrfRetry} from "./call_with_csrf_retry";
import {getNameSaveErrorMessage} from "./get_name_save_error_message";
import {isProfileNameFailure} from "./get_reservation_code_step_view";
import {createEmptyReservationDigits} from "./reservation_code_digits";
import {recordReservationCodeSend} from "./reservation_code_send_storage";

// what the CTA shows once the modal is closed: "taken" turns it into "reserved", "unavailable" hides it
const AVAILABILITY_AFTER_ERROR: Partial<Record<ReservationErrorKind, ReservationAvailabilityStatus>> = {
    taken: "taken",
    not_reservable: "unavailable"
};

const isCurrentRun = (ctx: IReservationFlowEngineContext, run: IReservationFlowRun) => run === ctx.runRef.current;

const callApi = <T>(ctx: IReservationFlowEngineContext, call: () => Promise<T>, phase: ReservationPhase, run: IReservationFlowRun) =>
    callWithCsrfRetry({call, phase, budget: run, isCurrent: () => isCurrentRun(ctx, run)});

const clearDigitsAndRequestFocus = (ctx: IReservationFlowEngineContext) => {
    ctx.codeEntry.setDigits(createEmptyReservationDigits());
    ctx.codeEntry.requestFocus();
};

const handleFailure = (ctx: IReservationFlowEngineContext, failedRequest: IReservationRequest, error: IReservationError, next: ReservationFlowState) => {
    ctx.tracking.failed(failedRequest, error);

    const availabilityAfterError = AVAILABILITY_AFTER_ERROR[error.kind];

    if (availabilityAfterError) {
        ctx.dispatch(setReservationAvailability(failedRequest.propertyId, availabilityAfterError));
    }

    if (error.kind === "unauthenticated") {
        // the resumed run continues this funnel, so it must not report another lead_form_start
        ctx.latestRef.current.onUnauthenticated({...failedRequest, isLeadFormStartTracked: true});
    }

    if (isProfileNameFailure(next)) {
        ctx.nameFormActions.rejected(error.message);
    }
};

const handleInitiateFailure = (ctx: IReservationFlowEngineContext, initiateRequest: IReservationRequest, error: IReservationError, isResend: boolean) => {
    const next = ctx.transitions.apply({type: isResend ? "RESEND_FAILURE" : "INITIATE_FAILURE", error, now: Date.now()});

    // the resend link had the focus and was disabled while the code was on its way - the inputs take it once they are enabled
    if (next.status === "code_entry") {
        ctx.codeEntry.requestFocus();
    }

    handleFailure(ctx, initiateRequest, error, next);
};

export const initiateReservation = async (ctx: IReservationFlowEngineContext, initiateRequest: IReservationRequest, isResend: boolean) => {
    const run = ctx.runRef.current;
    const result = await callApi(ctx, () => ctx.latestRef.current.postInitiate({property: initiateRequest.propertyId}).unwrap(), "initiate", run);

    if (result.error) {
        if (isCurrentRun(ctx, run)) {
            handleInitiateFailure(ctx, initiateRequest, result.error, isResend);
        }

        return;
    }

    // counted even when the modal has been closed in the meantime - the code is on its way
    const sentAt = Date.now();
    const codesSent = recordReservationCodeSend(initiateRequest.propertyId, sentAt);

    if (!isCurrentRun(ctx, run)) {
        return;
    }

    const {reservation_handle: handle, expires_in: expiresIn} = result.data;
    const next = ctx.transitions.applyChange({type: isResend ? "RESEND_SUCCESS" : "INITIATE_SUCCESS", handle, expiresIn, codesSent, now: sentAt});

    clearDigitsAndRequestFocus(ctx);

    if (!isResend) {
        ctx.tracking.codeSent(initiateRequest);
    } else if (next !== null) {
        ctx.codeEntry.setIsNewCodeSent(true);
    }
};

export const confirmReservation = async (ctx: IReservationFlowEngineContext, confirmRequest: IReservationRequest, attempt: IReservationConfirmAttempt) => {
    const run = ctx.runRef.current;
    const {handle, code} = attempt;
    const result = await callApi(ctx, () => ctx.latestRef.current.postConfirm({reservation_handle: handle, code}).unwrap(), "confirm", run);

    if (result.error) {
        if (!isCurrentRun(ctx, run)) {
            return;
        }

        const next = ctx.transitions.apply({type: "CONFIRM_FAILURE", error: result.error, now: Date.now()});

        if (next.status === "code_entry") {
            clearDigitsAndRequestFocus(ctx);
        }

        handleFailure(ctx, confirmRequest, result.error, next);
        return;
    }

    // the reservation exists even when the modal has been closed in the meantime
    const applicationUuid = result.data?.application_uuid || null;
    ctx.dispatch(markPropertyReservedByMe(confirmRequest.propertyId, Date.now()));
    // the cached availability follows too, so the CTA stays reserved once the marker is cleared, e.g. on logout before the redirect
    ctx.dispatch(setReservationAvailability(confirmRequest.propertyId, "taken"));

    if (isCurrentRun(ctx, run)) {
        ctx.transitions.apply({type: "CONFIRM_SUCCESS", applicationUuid});
    }

    ctx.tracking.confirmed(confirmRequest, applicationUuid);
};

// failed and retryable: confirm again with the same code while its handle is valid, otherwise initiate again
export const retryReservation = (ctx: IReservationFlowEngineContext) => {
    const {request: retryRequest} = ctx.latestRef.current;

    if (retryRequest === null) {
        return;
    }

    const next = ctx.transitions.applyCallStart({type: "RETRY", now: Date.now()});

    if (next === null) {
        return;
    }

    if (next.status === "confirming") {
        void confirmReservation(ctx, retryRequest, next);
        return;
    }

    void initiateReservation(ctx, retryRequest, false);
};

export const saveReservationName = async (ctx: IReservationFlowEngineContext, name: string, run: IReservationFlowRun) => {
    // a missing CSRF cookie is set again as for the reservation calls
    const result = await callApi(ctx, () => ctx.latestRef.current.patchUserProfile({name}).unwrap(), "initiate", run);

    // closed in the meantime - a code must not be sent for a reservation nobody can finish
    if (!isCurrentRun(ctx, run) || !ctx.isMountedRef.current) {
        return;
    }

    if (result.error) {
        ctx.nameFormActions.saveFailed(getNameSaveErrorMessage(result.error.raw.data));
        return;
    }

    ctx.nameFormActions.saved(name);
    retryReservation(ctx);
};
